/* eslint-disable no-console */
// Console log of the backend calls: which request, when, how long, how big. Off by default, so a
// normal session stays quiet; the measuring hooks are always installed but do nothing while off.
//
// Switch it in the browser console (remembered in this browser until switched off again):
//   pih.api(true)        - one line per call: method, path, status, duration, size
//   pih.api(false)       - off
//   pih.apiStats()       - table per route: calls, average/max duration, errors
//   pih.apiCalls(20)     - the last 20 calls as a table
// or for a single page load with ?apilog=1 (?apilog=0 switches it off again).
//
// Covers both ways the app talks to the backend: window.fetch (catalog without login, SAS token,
// documents) and the axios client that carries the login token (catalog with login, generated
// API). Images in <img> tags never pass through either - see pih.trace() for those.

import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

const STORAGE_KEY = 'pih.apilog';
const MAX_RECORDS = 500;
const SLOW_MS = 1000;

export interface ApiCallRecord {
    at: string;
    method: string;
    url: string;
    status: number | string;
    ms: number;
    bytes?: number;
    /** What actually crossed the wire - smaller than `bytes` when the response was compressed. */
    transferred?: number;
}

const readFlag = (): boolean => {
    if (typeof window === 'undefined') {
        return false;
    }
    try {
        const fromQuery = new URLSearchParams(window.location.search).get('apilog');
        if (fromQuery !== null) {
            const on = fromQuery !== '0' && fromQuery.toLowerCase() !== 'false';
            window.localStorage.setItem(STORAGE_KEY, on ? '1' : '');
            return on;
        }
        return window.localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
        return false;
    }
};

let enabled = readFlag();
const records: ApiCallRecord[] = [];

export const isApiLog = (): boolean => enabled;

export const setApiLog = (on: boolean): boolean => {
    enabled = on;
    try {
        window.localStorage.setItem(STORAGE_KEY, on ? '1' : '');
    } catch {
        // Storage blocked (private window) - then it only holds for this page.
    }
    console.info(on ? 'API log ON - every backend call is logged (pih.api(false) to stop).' : 'API log OFF.');
    return enabled;
};

const formatBytes = (bytes?: number): string => {
    if (bytes === undefined) {
        return '? B';
    }
    if (bytes < 1024) {
        return `${bytes} B`;
    }
    return bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(1)} KB` : `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

/** Origin removed for calls to the app's own host - the path is what tells calls apart. */
const shortUrl = (url: string): string => {
    try {
        const parsed = new URL(url, window.location.href);
        return parsed.origin === window.location.origin ? `${parsed.pathname}${parsed.search}` : parsed.href;
    } catch {
        return url;
    }
};

/**
 * Body size and transfer size from the browser's own resource timing. axios hands over a parsed
 * body without a byte count, and a compressed or chunked answer carries no Content-Length - so
 * this is the only place that knows both numbers. `transferSize` also shows whether the API
 * compresses at all (docs/11, point 1): a few percent of the body size means it does.
 */
const resourceSizes = (url: string): { bytes?: number; transferred?: number } => {
    try {
        const absolute = new URL(url, window.location.href).href;
        const entries = performance.getEntriesByName(absolute, 'resource') as PerformanceResourceTiming[];
        const last = entries[entries.length - 1];
        if (!last) {
            return {};
        }
        return {
            bytes: last.decodedBodySize > 0 ? last.decodedBodySize : undefined,
            transferred: last.transferSize > 0 ? last.transferSize : undefined
        };
    } catch {
        return {};
    }
};

const record = (method: string, url: string, status: number | string, ms: number, bytes?: number): void => {
    const measured = resourceSizes(url);
    const entry: ApiCallRecord = {
        at: new Date().toISOString().slice(11, 23),
        method: method.toUpperCase(),
        url: shortUrl(url),
        status,
        ms: Math.round(ms),
        bytes: bytes ?? measured.bytes,
        transferred: measured.transferred
    };
    records.push(entry);
    if (records.length > MAX_RECORDS) {
        records.shift();
    }

    const failed = typeof status !== 'number' || status >= 400;
    const color = failed ? '#c62828' : entry.ms >= SLOW_MS ? '#e65100' : '#2e7d32';
    const wire =
        entry.transferred !== undefined && entry.bytes !== undefined && entry.transferred < entry.bytes * 0.9
            ? ` (${formatBytes(entry.transferred)} transferred)`
            : '';
    console.log(
        `%c[API]%c ${entry.at} ${entry.method} ${entry.url} %c${status} · ${entry.ms} ms · ${formatBytes(entry.bytes)}${wire}`,
        'color:#004588;font-weight:600',
        'color:inherit',
        `color:${color};font-weight:600`
    );
};

/** The last calls, newest last - as a console table. */
export const apiCalls = (count = 20): ApiCallRecord[] => {
    const last = records.slice(-count);
    console.table(
        last.map((r) => ({
            ...r,
            size: formatBytes(r.bytes),
            transferredSize: r.transferred === undefined ? '' : formatBytes(r.transferred)
        }))
    );
    return last;
};

/** The time a route cost in total - the most expensive routes come first in apiStats. */
const totalMs = (row: { avgMs: number; calls: number }): number => row.avgMs * row.calls;

/** Calls per route (path without query): count, average, maximum, errors. */
export const apiStats = (): Record<string, unknown>[] => {
    const groups = new Map<string, ApiCallRecord[]>();
    for (const r of records) {
        const route = `${r.method} ${r.url.split('?')[0]}`;
        groups.set(route, [...(groups.get(route) ?? []), r]);
    }
    const rows = [...groups.entries()]
        .map(([route, calls]) => ({
            route,
            calls: calls.length,
            avgMs: Math.round(calls.reduce((sum, c) => sum + c.ms, 0) / calls.length),
            maxMs: Math.max(...calls.map((c) => c.ms)),
            errors: calls.filter((c) => typeof c.status !== 'number' || c.status >= 400).length,
            totalSize: formatBytes(calls.reduce((sum, c) => sum + (c.bytes ?? 0), 0)),
            transferred: formatBytes(calls.reduce((sum, c) => sum + (c.transferred ?? c.bytes ?? 0), 0))
        }))
        .sort((a, b) => totalMs(b) - totalMs(a));
    if (rows.length === 0) {
        console.info(enabled ? 'No backend calls recorded yet.' : 'API log is off - pih.api(true), then use the page.');
    } else {
        console.table(rows);
    }
    return rows;
};

let fetchInstalled = false;

/**
 * Wraps window.fetch once. While the log is on, the response is cloned and read to the end, so the
 * duration covers the whole download and the size is the real body size; while off, the call goes
 * straight through.
 */
export const installFetchLog = (): void => {
    if (fetchInstalled || typeof window === 'undefined') {
        return;
    }
    fetchInstalled = true;
    // The browser keeps 250 resource entries by default; a catalog page with its images passes that
    // quickly, and later calls would then lose their sizes (see resourceSizes above, pih.stats too).
    performance.setResourceTimingBufferSize?.(5000);
    const original = window.fetch.bind(window);

    window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
        if (!enabled) {
            return original(input, init);
        }
        const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
        const method = init?.method ?? (input instanceof Request ? input.method : 'GET');
        const start = performance.now();
        try {
            const response = await original(input, init);
            response
                .clone()
                .arrayBuffer()
                .then(
                    (body) => record(method, url, response.status, performance.now() - start, body.byteLength),
                    () => record(method, url, response.status, performance.now() - start)
                );
            return response;
        } catch (cause) {
            record(method, url, 'network error', performance.now() - start);
            throw cause;
        }
    };
};

type TimedConfig = InternalAxiosRequestConfig & { pihStart?: number };

const contentLength = (response?: AxiosResponse): number | undefined => {
    const header = response?.headers?.['content-length'];
    const parsed = header === undefined ? NaN : Number(header);
    return Number.isFinite(parsed) ? parsed : undefined;
};

const axiosUrl = (config?: InternalAxiosRequestConfig): string =>
    config ? `${config.baseURL ?? ''}${config.url ?? ''}` : '(unknown)';

/** Times every call through the given axios client (the one that carries the login token). */
export const installAxiosLog = (client: AxiosInstance): void => {
    client.interceptors.request.use((config: TimedConfig) => {
        config.pihStart = performance.now();
        return config;
    });
    client.interceptors.response.use(
        (response) => {
            const config = response.config as TimedConfig;
            if (enabled && config.pihStart !== undefined) {
                record(config.method ?? 'get', axiosUrl(config), response.status, performance.now() - config.pihStart, contentLength(response));
            }
            return response;
        },
        (error: { config?: TimedConfig; response?: AxiosResponse }) => {
            const config = error.config;
            if (enabled && config?.pihStart !== undefined) {
                record(
                    config.method ?? 'get',
                    axiosUrl(config),
                    error.response?.status ?? 'network error',
                    performance.now() - config.pihStart,
                    contentLength(error.response)
                );
            }
            return Promise.reject(error);
        }
    );
};
